import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus } from "lucide-react";
import { apiClient } from "../lib/api";
import { toast } from "sonner";
import { todayISO } from "../lib/format";

export default function ClientCreateDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    pin: "",
    start_date: todayISO(),
    initial_capital: "",
    annual_fee_pct: "",
    notes: "",
  });

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!form.name || !form.pin || !form.start_date) {
      toast.error("Name, PIN and start date are required");
      return;
    }
    if (!/^\d{4}$/.test(form.pin)) {
      toast.error("PIN must be 4 digits");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        pin: form.pin,
        start_date: form.start_date,
        initial_capital: Number(form.initial_capital) || 0,
        annual_fee_pct:
          form.annual_fee_pct === "" ? null : Number(form.annual_fee_pct),
        notes: form.notes || null,
      };
      if (form.email && form.email.trim()) payload.email = form.email.trim();
      const { data } = await apiClient.post("/advisor/clients", payload);
      toast.success(`Client ${data.name} created — code ${data.client_code}`);
      setOpen(false);
      setForm({
        name: "",
        email: "",
        pin: "",
        start_date: todayISO(),
        initial_capital: "",
        annual_fee_pct: "",
        notes: "",
      });
      onCreated?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create client");
    } finally {
      setLoading(false);
    }
  };

  const field = (key, label, type = "text", placeholder = "", extra = "", optional = false) => (
    <div className={extra}>
      <label className="text-[11px] uppercase-track text-muted-foreground font-medium">
        {label} {optional ? <span className="text-muted-foreground/70 normal-case">(optional)</span> : null}
      </label>
      <input
        data-testid={`client-${key}`}
        type={type}
        value={form[key]}
        onChange={(e) => set(key, e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full border border-border px-3 py-2 text-sm bg-card focus:outline-none focus:border-ring tabular"
      />
    </div>
  );

  return (
    <>
      <button
        data-testid="create-client-btn"
        onClick={() => setOpen(true)}
        className="bg-lime hover:brightness-110 text-white px-4 py-2.5 text-xs font-bold uppercase tracking-[0.2em] rounded-md flex items-center gap-2 active:scale-[0.98]"
      >
        <Plus className="h-4 w-4" strokeWidth={1.5} />
        New Client
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-none bg-background">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif font-medium tracking-tight">
              Add new client
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {field("name", "Full name", "text", "Rohan Mehta")}
            {field("email", "Email", "email", "rohan@example.com", "", true)}
            {field("pin", "4-digit PIN", "password", "••••")}
            {field("start_date", "Portfolio start date", "date")}
            {field("initial_capital", "Initial capital (₹)", "number", "1000000")}
            {field("annual_fee_pct", "Annual advisory fee (%)", "number", "2.00")}
            <div className="col-span-2 border-t border-subtle pt-3">
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                <strong>Starting benchmark levels</strong> (NIFTY 50, SENSEX, NIFTY 500)
                will be auto-fetched from Yahoo Finance for{" "}
                <span className="font-mono">{form.start_date || "the start date"}</span>.
              </p>
              <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                Clients log in with their <strong>4-digit PIN</strong> only — PINs are auto-checked
                to be unique across all clients. Advisory fee is billed half-yearly (Apr–Sep, Oct–Mar);
                mid-period capital additions are pro-rated.
              </p>
            </div>
            <div className="col-span-2">
              <label className="text-[11px] uppercase-track text-muted-foreground font-medium">
                Notes
              </label>
              <textarea
                data-testid="client-notes"
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                rows={2}
                className="mt-1 w-full border border-border px-3 py-2 text-sm bg-card focus:outline-none focus:border-ring"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <button
              onClick={() => setOpen(false)}
              className="border border-border hover:bg-secondary px-4 py-2 text-xs font-medium uppercase-track"
            >
              Cancel
            </button>
            <button
              data-testid="client-create-submit"
              onClick={submit}
              disabled={loading}
              className="bg-lime hover:brightness-110 text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] rounded-md disabled:opacity-50 active:scale-[0.98]"
            >
              {loading ? "Creating…" : "Create client"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
